import {
  UsageDetailsData,
  ModelUsageDetail,
  ApiKeyUsageDetail,
  DailyUsageData,
  UsageProvider,
} from '../../../../utils/openai-api';
import { EmployeeRecord } from '@/utils/employee-indexeddb';
import { mergeUsageForCharts } from '@/utils/usage-merge';
import { matchesOrganizationFilter } from '../../utils/organization';
import { formatLocalDateWithTime } from '../../utils/formatters';
import { UsageDataSource } from '../../types';
import { FilteredDataResult } from './types';
import { getApiKeyUniqueId, mergeApiKeysByName } from './mergeApiKeys';
import { findEmployeeForApiKeyFn } from './findEmployee';
import { debugLog } from '../../../../utils/debug-log';
import { OPENAI_WEB_SEARCH_COST_MODEL } from '../../../../utils/openai-api/web-search';

export interface ComputeFilteredDataParams {
  data: UsageDetailsData | null;
  getDateRange: () => { startDate: Date; endDate: Date; label: string };
  excludedModels: string[];
  selectedWorkspace: string;
  selectedModel: string;
  filter: string;
  normalizedDepartmentFilter: string;
  selectedRole: string;
  employeeNameFilter: string;
  employeeData: Map<string, EmployeeRecord>;
  dataSource: UsageDataSource;
}

/**
 * Pure computation of the filtered/aggregated usage view.
 *
 * Extracted verbatim from the former `useFilteredData` useMemo so the hook
 * stays a thin orchestrator. Depends only on its params (no React state).
 */
export function computeFilteredData({
  data,
  getDateRange,
  excludedModels,
  selectedWorkspace,
  selectedModel,
  filter,
  normalizedDepartmentFilter,
  selectedRole,
  employeeNameFilter,
  employeeData,
  dataSource,
}: ComputeFilteredDataParams): FilteredDataResult | null {
  if (!data) return null;

  if (import.meta.env.DEV) debugLog('Filtering data - original dailyUsage:', data.dailyUsage?.map(d => ({
    date: d.date,
    cost: d.cost,
    requests: d.requests,
    uniqueApiKeys: d.uniqueApiKeys,
    modelsCount: Object.keys(d.models).length
  })));

  const { startDate, endDate, label } = getDateRange();
  const startTime = startDate.getTime();
  const endTime = endDate.getTime();
  const endBoundary = endTime + 60 * 1000;
  const hasExplicitTime = (value: string): boolean => value.includes(':');
  const parseUsageTimestamp = (value: string): number => {
    const [datePart, timePart = '00:00:00'] = value.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour = 0, minute = 0, second = 0] = timePart.split(':').map(Number);
    return Date.UTC(year, month - 1, day, hour, minute, second, 0);
  };
  const clampUsageToRange = (entries: DailyUsageData[]): DailyUsageData[] => {
    return entries.filter(entry => {
      const timestamp = parseUsageTimestamp(entry.date);
      const upperBound = hasExplicitTime(entry.date) ? endBoundary : endTime;
      return timestamp >= startTime && timestamp <= upperBound;
    });
  };

  const workspaceAndModelFilteredKeys = data.apiKeyUsage.filter(apiKey => {
    if (filter !== '') {
      try {
        const regex = new RegExp(filter, 'i');
        if (!regex.test(apiKey.name)) {
          return false;
        }
      } catch  {
        if (!apiKey.name.toLowerCase().includes(filter.toLowerCase())) {
          return false;
        }
      }
    }

    if (selectedWorkspace !== 'all') {
      const workspaceIds = new Set<string>();
      if (apiKey.projectId) {
        workspaceIds.add(apiKey.projectId);
      }
      if (Array.isArray(apiKey.projectIds)) {
        apiKey.projectIds.forEach(id => {
          if (id) workspaceIds.add(id);
        });
      }

      if (selectedWorkspace.startsWith('wsname:')) {
        // Name-based match: resolve to all project IDs sharing this name
        const wsName = selectedWorkspace.slice(7);
        const matchingIds = data.projects
          ?.filter(p => p.name === wsName)
          .map(p => p.id) ?? [];
        if (!matchingIds.some(id => workspaceIds.has(id))) {
          return false;
        }
      } else if (!workspaceIds.has(selectedWorkspace)) {
        return false;
      }
    }

    if (selectedModel !== 'all') {
      const hasModel = Object.keys(apiKey.modelUsage || {}).includes(selectedModel);
      if (!hasModel) {
        return false;
      }
    }

    const employee = findEmployeeForApiKeyFn(apiKey, employeeData);

    if (!matchesOrganizationFilter(employee, normalizedDepartmentFilter)) {
      return false;
    }

    if (selectedRole !== '') {
      if (!employee || !employee.role) return false;

      try {
        const regex = new RegExp(selectedRole, 'i');
        if (!regex.test(employee.role)) {
          return false;
        }
      } catch  {
        if (!employee.role.toLowerCase().includes(selectedRole.toLowerCase())) {
          return false;
        }
      }
    }

    if (employeeNameFilter !== '') {
      if (!employee) return false;

      try {
        const regex = new RegExp(employeeNameFilter, 'i');
        if (!regex.test(employee.name || '')) {
          return false;
        }
      } catch  {
        if (!employee.name?.toLowerCase().includes(employeeNameFilter.toLowerCase())) {
          return false;
        }
      }
    }

    return true;
  });

  const filteredApiKeyIds = new Set(workspaceAndModelFilteredKeys.map(getApiKeyUniqueId));



  const filteredApiKeyUsage = workspaceAndModelFilteredKeys.map(apiKey => {
    const filteredModelUsageForKey: typeof apiKey.modelUsage = {};
    let filteredCost = 0;
    let filteredRequests = 0;
    let filteredContextTokens = 0;
    let filteredGeneratedTokens = 0;
    let filteredCachedTokens = 0;

    Object.entries(apiKey.modelUsage).forEach(([modelName, modelData]) => {
      if (!excludedModels.includes(modelName)) {
        filteredModelUsageForKey[modelName] = { ...modelData };
        filteredCost += modelData.cost;
        filteredRequests += modelData.numberOfRequests;
        filteredContextTokens += modelData.contextTokens;
        filteredGeneratedTokens += modelData.generatedTokens;
        filteredCachedTokens += modelData.cachedTokens;
      }
    });

    return {
      ...apiKey,
      modelUsage: filteredModelUsageForKey,
      totalCost: filteredCost,
      numberOfRequests: filteredRequests,
      requests: filteredRequests,
      contextTokens: filteredContextTokens,
      generatedTokens: filteredGeneratedTokens,
      cachedTokens: filteredCachedTokens,
      cachedPercentage: filteredCachedTokens > 0 && filteredContextTokens > 0
        ? (filteredCachedTokens / filteredContextTokens) * 100
        : 0,
      costPer100Requests: filteredRequests > 0 ? (filteredCost / filteredRequests) * 100 : 0,
      status: filteredRequests > 0 ? 'Active' as const : 'Inactive' as const
    };
  });

  const activeFilteredApiKeyUsage = filteredApiKeyUsage.filter(apiKey => {
    const uniqueId = getApiKeyUniqueId(apiKey);
    if (!filteredApiKeyIds.has(uniqueId)) {
      if (apiKey.provider === 'mixed' && Array.isArray(apiKey.sourceIds)) {
        const matchesSource = apiKey.sourceIds.some(sourceId =>
          filteredApiKeyIds.has(`openai:${sourceId}`) ||
          filteredApiKeyIds.has(`anthropic:${sourceId}`) ||
          filteredApiKeyIds.has(`unknown:${sourceId}`)
        );
        if (!matchesSource) {
          return false;
        }
      } else {
        return false;
      }
    }
    return true;
  });

  const mergedApiKeyUsage = mergeApiKeysByName(activeFilteredApiKeyUsage);
  const activeApiKeyIdsForDaily = new Set<string>();
  activeFilteredApiKeyUsage.forEach(apiKey => {
    activeApiKeyIdsForDaily.add(apiKey.id);
    if (Array.isArray(apiKey.sourceIds)) {
      apiKey.sourceIds.forEach(sourceId => {
        activeApiKeyIdsForDaily.add(sourceId);
      });
    }
  });

  const modelUsageFromFilteredKeys = new Map<string, ModelUsageDetail>();
  mergedApiKeyUsage.forEach(apiKey => {
    Object.entries(apiKey.modelUsage).forEach(([modelName, modelData]) => {
      const providerKey = `${modelName}:${modelData.provider ?? 'unknown'}`;
      if (!modelUsageFromFilteredKeys.has(providerKey)) {
        modelUsageFromFilteredKeys.set(providerKey, {
          model: modelName,
          contextTokens: 0,
          generatedTokens: 0,
          cachedTokens: 0,
          cost: 0,
          numberOfRequests: 0,
          provider: modelData.provider
        });
      }
      const existing = modelUsageFromFilteredKeys.get(providerKey)!;
      if (!existing.provider && modelData.provider) {
        existing.provider = modelData.provider;
      }
      existing.contextTokens += modelData.contextTokens;
      existing.generatedTokens += modelData.generatedTokens;
      existing.cachedTokens += modelData.cachedTokens;
      existing.cost += modelData.cost;
      existing.numberOfRequests += modelData.numberOfRequests;
      if (modelData.lastUsed && (!existing.lastUsed || modelData.lastUsed > existing.lastUsed)) {
        existing.lastUsed = modelData.lastUsed;
      }
    });
  });
  const recalculatedModelUsage = Array.from(modelUsageFromFilteredKeys.values());
  // Costs quantities belong to the full project/day scope. Key-level cost allocation
  // is estimated, so a subset of contributors must not inherit the full search count.
  const searchUsage = recalculatedModelUsage.find(model =>
    model.provider === 'openai' && model.model === OPENAI_WEB_SEARCH_COST_MODEL);
  if (searchUsage) {
    const allSearchContributorsIncluded = data.apiKeyUsage
      .filter(key => key.modelUsage[OPENAI_WEB_SEARCH_COST_MODEL]?.provider === 'openai')
      .every(key => filteredApiKeyIds.has(getApiKeyUniqueId(key)));
    searchUsage.webSearchCalls = allSearchContributorsIncluded
      ? data.modelUsage.find(model => model.provider === 'openai'
        && model.model === OPENAI_WEB_SEARCH_COST_MODEL)?.webSearchCalls ?? null
      : null;
  }

  const sumProviderTotals = (items: ApiKeyUsageDetail[]) => {
    const totals = ['openai', 'anthropic'].reduce((acc, providerKey) => {
      acc[providerKey as UsageProvider] = {
        requests: 0,
        cost: 0,
        contextTokens: 0,
        generatedTokens: 0,
        cachedTokens: 0
      };
      return acc;
    }, {} as Record<UsageProvider, {
      requests: number;
      cost: number;
      contextTokens: number;
      generatedTokens: number;
      cachedTokens: number;
    }>);
    items.forEach(item => {
      const providerKey: UsageProvider = item.provider === 'anthropic' ? 'anthropic' : 'openai';
      totals[providerKey].requests += item.numberOfRequests ?? item.requests ?? 0;
      totals[providerKey].cost += (item.totalCost ?? item.cost ?? 0);
      totals[providerKey].contextTokens += item.contextTokens ?? 0;
      totals[providerKey].generatedTokens += item.generatedTokens ?? 0;
      totals[providerKey].cachedTokens += item.cachedTokens ?? 0;
    });
    return totals;
  };
  const overallProviderTotals = sumProviderTotals(data.apiKeyUsage ?? []);
  const filteredProviderTotals = sumProviderTotals(activeFilteredApiKeyUsage);

  const computeRatio = (filteredValue: number, totalValue: number) => {
    if (!Number.isFinite(filteredValue) || filteredValue <= 0) {
      return 0;
    }
    if (!Number.isFinite(totalValue) || totalValue <= 0) {
      return 1;
    }
    return Math.min(1, filteredValue / totalValue);
  };

  const providerRatios: Record<UsageProvider, {
    requests: number;
    cost: number;
    contextTokens: number;
    generatedTokens: number;
    cachedTokens: number;
  }> = {
    openai: {
      requests: computeRatio(filteredProviderTotals.openai.requests, overallProviderTotals.openai.requests),
      cost: computeRatio(filteredProviderTotals.openai.cost, overallProviderTotals.openai.cost),
      contextTokens: computeRatio(filteredProviderTotals.openai.contextTokens, overallProviderTotals.openai.contextTokens),
      generatedTokens: computeRatio(filteredProviderTotals.openai.generatedTokens, overallProviderTotals.openai.generatedTokens),
      cachedTokens: computeRatio(filteredProviderTotals.openai.cachedTokens, overallProviderTotals.openai.cachedTokens)
    },
    anthropic: {
      requests: computeRatio(filteredProviderTotals.anthropic.requests, overallProviderTotals.anthropic.requests),
      cost: computeRatio(filteredProviderTotals.anthropic.cost, overallProviderTotals.anthropic.cost),
      contextTokens: computeRatio(filteredProviderTotals.anthropic.contextTokens, overallProviderTotals.anthropic.contextTokens),
      generatedTokens: computeRatio(filteredProviderTotals.anthropic.generatedTokens, overallProviderTotals.anthropic.generatedTokens),
      cachedTokens: computeRatio(filteredProviderTotals.anthropic.cachedTokens, overallProviderTotals.anthropic.cachedTokens)
    }
  };

  const enabledProviders: UsageProvider[] = dataSource === 'all'
    ? ['openai', 'anthropic']
    : dataSource === 'anthropic'
      ? ['anthropic']
      : ['openai'];

  const baseDailyUsage = (data.dailyUsage || []).map(day => ({
    ...day,
    models: { ...(day.models || {}) },
    breakdowns: day.breakdowns
      ? Object.fromEntries(
          Object.entries(day.breakdowns).map(([key, metrics]) => [
            key,
            { ...metrics }
          ])
        )
      : undefined,
    apiKeys: day.apiKeys instanceof Set
      ? new Set<string>(day.apiKeys)
      : new Set<string>(Array.isArray(day.apiKeys) ? day.apiKeys : []),
    uniqueApiKeys: day.apiKeys instanceof Set
      ? day.apiKeys.size
      : day.uniqueApiKeys
  }));

  const hasActiveFilters = selectedWorkspace !== 'all' ||
    selectedModel !== 'all' ||
    filter !== '' ||
    excludedModels.length > 0 ||
    enabledProviders.some(provider => providerRatios[provider].requests < 1);

  const roundNumber = (value: number, digits = 2) => {
    if (!Number.isFinite(value)) return 0;
    return Number(value.toFixed(digits));
  };

  const apiKeyActiveRanges = mergedApiKeyUsage
    .map(apiKey => {
      const start = parseUsageTimestamp(apiKey.firstSeen ?? '');
      if (!Number.isFinite(start)) {
        return null;
      }
      const rawEnd = parseUsageTimestamp(apiKey.lastSeen ?? '');
      const endBase = Number.isFinite(rawEnd) ? rawEnd : start;
      const end = new Date(endBase).setHours(23, 59, 59, 999);
      return Number.isFinite(end)
        ? { start, end }
        : null;
    })
    .filter((range): range is { start: number; end: number } => !!range);

  const normalizeDailyEntry = (day: DailyUsageData): DailyUsageData => {
    const apiKeySet = day.apiKeys instanceof Set
      ? new Set<string>(day.apiKeys)
      : new Set<string>(Array.isArray(day.apiKeys) ? day.apiKeys : []);
    const parsedUnique = Number(day.uniqueApiKeys);
    const rawUnique = Number.isFinite(parsedUnique) ? Math.max(0, Math.round(parsedUnique)) : 0;
    let normalizedUnique = apiKeySet.size > 0 ? apiKeySet.size : rawUnique;
    const isHourlyBucket = day.date.includes(':');

    if (!isHourlyBucket && normalizedUnique <= 0 && apiKeyActiveRanges.length > 0) {
      const bucketTimestamp = parseUsageTimestamp(day.date);
      if (Number.isFinite(bucketTimestamp)) {
        const fallbackCount = apiKeyActiveRanges.reduce((count, range) => {
          return bucketTimestamp >= range.start && bucketTimestamp <= range.end
            ? count + 1
            : count;
        }, 0);
        if (fallbackCount > 0) {
          normalizedUnique = fallbackCount;
        }
      }
    }
    const breakdowns = day.breakdowns
      ? Object.fromEntries(Object.entries(day.breakdowns).map(([key, metrics]) => [
          key,
          {
            ...metrics,
            requests: roundNumber(metrics.requests ?? 0, 2),
            cost: roundNumber(metrics.cost ?? 0, 4),
            contextTokens: Math.round(metrics.contextTokens ?? 0),
            generatedTokens: Math.round(metrics.generatedTokens ?? 0),
            cachedTokens: Math.round(metrics.cachedTokens ?? 0),
            isEstimatedRequests: metrics.isEstimatedRequests
          }
        ]))
      : undefined;
    const isEstimated = breakdowns ? Object.values(breakdowns).some(metric => metric?.isEstimatedRequests) : day.isEstimatedRequests;
    const normalizedRequests = hasActiveFilters
      ? roundNumber(day.requests ?? 0, 2)
      : Math.round(day.requests ?? 0);
    const normalizedNumRequests = hasActiveFilters
      ? roundNumber(day.num_requests ?? day.requests ?? 0, 2)
      : Math.round(day.num_requests ?? day.requests ?? 0);
    return {
      ...day,
      requests: normalizedRequests,
      cost: roundNumber(day.cost ?? 0, 4),
      contextTokens: Math.round(day.contextTokens ?? 0),
      generatedTokens: Math.round(day.generatedTokens ?? 0),
      cachedTokens: Math.round(day.cachedTokens ?? 0),
      models: Object.fromEntries(Object.entries(day.models || {}).map(([model, value]) => [model, roundNumber(value ?? 0, 2)])),
      uniqueApiKeys: normalizedUnique,
      apiKeys: apiKeySet,
      num_requests: normalizedNumRequests,
      breakdowns,
      isEstimatedRequests: isEstimated
    };
  };

  let filteredDailyUsage: DailyUsageData[];
  let chartDailyUsage: DailyUsageData[];

  if (!hasActiveFilters) {
    filteredDailyUsage = clampUsageToRange(baseDailyUsage.map(normalizeDailyEntry));
    chartDailyUsage = filteredDailyUsage;
  } else {
    const providerSeriesMap: Record<UsageProvider, DailyUsageData[]> = {
      openai: [],
      anthropic: []
    };

    baseDailyUsage.forEach(day => {
      const dayTotalRequests = Object.values(day.breakdowns || {}).reduce((sum, metrics) => sum + (metrics?.requests ?? 0), 0) || day.requests || 0;
      const dayApiKeys = day.apiKeys instanceof Set
        ? new Set<string>(day.apiKeys)
        : new Set<string>(Array.isArray(day.apiKeys) ? day.apiKeys : []);
      const filteredDayApiKeys = new Set<string>(
        Array.from(dayApiKeys).filter(apiKeyId => activeApiKeyIdsForDaily.has(apiKeyId))
      );
      enabledProviders.forEach(provider => {
        const ratios = providerRatios[provider];
        const baseBreakdown = day.breakdowns?.[provider];
        const baseRequests = baseBreakdown?.requests ?? 0;
        const providerShare = dayTotalRequests > 0 ? baseRequests / dayTotalRequests : (enabledProviders.length === 1 ? 1 : 0);

        const scaledRequests = baseRequests * ratios.requests;
        const scaledCost = (baseBreakdown?.cost ?? 0) * ratios.cost;
        const scaledContextTokens = (baseBreakdown?.contextTokens ?? 0) * ratios.contextTokens;
        const scaledGeneratedTokens = (baseBreakdown?.generatedTokens ?? 0) * ratios.generatedTokens;
        const scaledCachedTokens = (baseBreakdown?.cachedTokens ?? 0) * ratios.cachedTokens;

        const scaledModels: Record<string, number> = {};
        Object.entries(day.models || {}).forEach(([modelName, value]) => {
          if (excludedModels.includes(modelName)) {
            return;
          }
          const contribution = (value ?? 0) * providerShare * ratios.requests;
          if (contribution > 0) {
            scaledModels[modelName] = contribution;
          }
        });

        const scaledModelCosts: Record<string, number> = {};
        if (day.modelCosts) {
          Object.entries(day.modelCosts).forEach(([modelName, value]) => {
            if (excludedModels.includes(modelName)) return;
            const contribution = (value ?? 0) * providerShare * ratios.cost;
            if (contribution > 0) {
              scaledModelCosts[modelName] = contribution;
            }
          });
        }

        providerSeriesMap[provider].push({
          date: day.date,
          requests: scaledRequests,
          cost: scaledCost,
          contextTokens: scaledContextTokens,
          generatedTokens: scaledGeneratedTokens,
          cachedTokens: scaledCachedTokens,
          models: scaledModels,
          modelCosts: Object.keys(scaledModelCosts).length > 0 ? scaledModelCosts : undefined,
          uniqueApiKeys: filteredDayApiKeys.size,
          apiKeys: filteredDayApiKeys,
          provider,
          breakdowns: {
            [provider]: {
              provider,
              requests: scaledRequests,
              cost: scaledCost,
              contextTokens: scaledContextTokens,
              generatedTokens: scaledGeneratedTokens,
              cachedTokens: scaledCachedTokens,
              isEstimatedRequests: baseBreakdown?.isEstimatedRequests ?? (provider === 'anthropic')
            }
          },
          isEstimatedRequests: baseBreakdown?.isEstimatedRequests ?? (provider === 'anthropic'),
          num_requests: scaledRequests
        });
      });
    });

    const chartSources = enabledProviders
      .map(provider => ({ provider, dailyUsage: providerSeriesMap[provider] }))
      .filter(source => source.dailyUsage.length > 0);

    const mergedSeries = chartSources.length > 0
      ? mergeUsageForCharts(chartSources, { join: 'outer' })
      : baseDailyUsage;

    filteredDailyUsage = clampUsageToRange(mergedSeries.map(normalizeDailyEntry));
    chartDailyUsage = filteredDailyUsage;
  }

  if (import.meta.env.DEV) debugLog('Filtering data - filtered dailyUsage:', filteredDailyUsage?.map(d => ({
    date: d.date,
    cost: d.cost,
    requests: d.requests,
    uniqueApiKeys: d.uniqueApiKeys,
    apiKeySetSize: d.apiKeys instanceof Set ? d.apiKeys.size : null,
    modelsCount: Object.keys(d.models).length
  })));

  if (import.meta.env.DEV && filteredDailyUsage.length > 0 && filteredDailyUsage[0].date.includes(':')) {
    debugLog('Hour data debug:', {
      firstItem: filteredDailyUsage[0],
      totalItems: filteredDailyUsage.length,
      totalRequests: filteredDailyUsage.reduce((sum, d) => sum + d.requests, 0),
      totalCost: filteredDailyUsage.reduce((sum, d) => sum + d.cost, 0)
    });
  }

  const totalCost = mergedApiKeyUsage.reduce((sum, key) => sum + (key.totalCost ?? key.cost), 0);
  const totalRequests = mergedApiKeyUsage.reduce((sum, key) => sum + key.numberOfRequests, 0);
  const totalContextTokens = mergedApiKeyUsage.reduce((sum, key) => sum + key.contextTokens, 0);
  const totalGeneratedTokens = mergedApiKeyUsage.reduce((sum, key) => sum + key.generatedTokens, 0);
  const totalCachedTokens = mergedApiKeyUsage.reduce((sum, key) => sum + key.cachedTokens, 0);

  return {
    ...data,
    modelUsage: recalculatedModelUsage,
    apiKeyUsage: mergedApiKeyUsage,
    totalCost,
    totalRequests,
    totalContextTokens,
    totalGeneratedTokens,
    totalCachedTokens,
    totalApiKeys: mergedApiKeyUsage.length,
    displayedApiKeys: mergedApiKeyUsage.length,
    dailyUsage: filteredDailyUsage,
    chartDailyUsage,
    dateRangeInfo: {
      startDateIso: startDate.toISOString(),
      endDateIso: endDate.toISOString(),
      startDateLabel: formatLocalDateWithTime(startDate),
      endDateLabel: formatLocalDateWithTime(endDate),
      label,
      timezoneOffsetMinutes: -startDate.getTimezoneOffset()
    }
  };
}
